import { Injectable } from '@angular/core';
import { Base64TextConverter, RequestRoot } from 'types';
import { PngImageBuilder } from './png-image-builder';
import { PrintQRCodeGenerationService } from 'src/webid/4.0/model/print-web/service/print-qrcode-generation.service';
import { RequestToQrCodeStringConverter } from 'src/webid/4.0/model/qrcode/qr-code.converter';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { version } from 'src/version';
import { IPrintDepartment } from 'src/webid/4.0/model/print-web/model/print-department';

declare var QRCode: any;

@Injectable()
export class QRCodeGenerationService extends PrintQRCodeGenerationService {
    private readonly PRINT_DOTS_NORMAL: number = 4;
    private readonly PRINT_DOTS_LARGE: number = 5;
    private readonly PRINT_DOTS_HUGE: number = 6;
    private readonly PRINT_DPI: number = 300;
    private readonly SCREEN_DPI: number = 96;
    private readonly QR_ITEM_SIZE: number = 2;
    private readonly FONT_LINE_HEIGHT: number = 16;

    private readonly INET_BITMAP_WIDTH: number = this.QR_ITEM_SIZE * 61;
    private readonly INET_BITMAP_HEIGHT: number = this.QR_ITEM_SIZE * 61;
    private readonly INET_IMAGE_WIDTH: number = Math.floor(
        (this.INET_BITMAP_WIDTH * this.PRINT_DOTS_NORMAL * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly INET_IMAGE_HEIGHT: number = Math.floor(
        (this.INET_BITMAP_HEIGHT * this.PRINT_DOTS_NORMAL * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );

    private readonly EMBED_BITMAP_WIDTH: number = this.QR_ITEM_SIZE * 177 + this.FONT_LINE_HEIGHT * 2;
    private readonly EMBED_BITMAP_HEIGHT: number = this.QR_ITEM_SIZE * 177 + this.FONT_LINE_HEIGHT * 8;
    private readonly EMBED_IMAGE_WIDTH_NORMAL: number = Math.floor(
        (this.EMBED_BITMAP_WIDTH * this.PRINT_DOTS_NORMAL * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly EMBED_IMAGE_HEIGHT_NORMAL: number = Math.floor(
        (this.EMBED_BITMAP_HEIGHT * this.PRINT_DOTS_NORMAL * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly EMBED_IMAGE_WIDTH_LARGE: number = Math.floor(
        (this.EMBED_BITMAP_WIDTH * this.PRINT_DOTS_LARGE * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly EMBED_IMAGE_HEIGHT_LARGE: number = Math.floor(
        (this.EMBED_BITMAP_HEIGHT * this.PRINT_DOTS_LARGE * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly EMBED_IMAGE_WIDTH_HUGE: number = Math.floor(
        (this.EMBED_BITMAP_WIDTH * this.PRINT_DOTS_HUGE * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );
    private readonly EMBED_IMAGE_HEIGHT_HUGE: number = Math.floor(
        (this.EMBED_BITMAP_HEIGHT * this.PRINT_DOTS_HUGE * this.SCREEN_DPI) / (this.QR_ITEM_SIZE * this.PRINT_DPI)
    );

    private converter: RequestToQrCodeStringConverter;

    constructor() {
        super();
        this.converter = new RequestToQrCodeStringConverter();
    }

    getPlainQrCode(request: RequestRoot, department: IPrintDepartment): string | QRCodeError {
        try {
            let data = this.converter.toString(request, department, { website: version });
            return this.generatePlainQRCode(data);
        } catch (e) {
            return e === 'too large data' ? QRCodeError.TooLarge : QRCodeError.Unknown;
        }
    }

    getEmbedQrCode(request: RequestRoot, department: IPrintDepartment): string | QRCodeError {
        try {
            let text = this.createQrCodeInfo(request);
            let data = this.converter.toString(request, department, { website: '' });
            return this.generateQRCodeWithText(text, data, this.EMBED_BITMAP_WIDTH, this.EMBED_BITMAP_HEIGHT);
        } catch (e) {
            return e === 'too large data' ? QRCodeError.TooLarge : QRCodeError.Unknown;
        }
    }

    getInetQrCode(request: RequestRoot): string | QRCodeError {
        if (request.uid && request.uid.length !== 25) {
            return (
                'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd' +
                '1PeAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7D' +
                'AcdvqGQAAAAMSURBVBhXY/j//z8ABf4C/qc1gYQAAAAASUVORK5CYII='
            );
        }

        try {
            let data = this.converter.toInetString(request);
            return this.generateQRCodeWithText([], data, this.INET_BITMAP_WIDTH, this.INET_BITMAP_HEIGHT);
        } catch (e) {
            return e === 'too large data' ? QRCodeError.TooLarge : QRCodeError.Unknown;
        }
    }

    getInetImageWidth(): number {
        return this.INET_IMAGE_WIDTH;
    }

    getInetImageHeight(): number {
        return this.INET_IMAGE_HEIGHT;
    }

    getEmbedImageWidthNormal(): number {
        return this.EMBED_IMAGE_WIDTH_NORMAL;
    }

    getEmbedImageHeightNormal(): number {
        return this.EMBED_IMAGE_HEIGHT_NORMAL;
    }

    getEmbedImageWidthLarge(): number {
        return this.EMBED_IMAGE_WIDTH_LARGE;
    }

    getEmbedImageHeightLarge(): number {
        return this.EMBED_IMAGE_HEIGHT_LARGE;
    }

    getEmbedImageWidthHuge(): number {
        return this.EMBED_IMAGE_WIDTH_HUGE;
    }

    getEmbedImageHeightHuge(): number {
        return this.EMBED_IMAGE_HEIGHT_HUGE;
    }

    private generatePlainQRCode(data: string): string {
        // compress data
        var base64TextConverter = new Base64TextConverter();
        let dataStrZIP: string = base64TextConverter.toCompressedBase64(data);

        // prepare QR
        let qrData: any = new QRCode.generate(dataStrZIP);
        let qrSize: number = qrData.length * this.QR_ITEM_SIZE;

        // draw QR
        let bitmap = new PngImageBuilder(qrSize, qrSize);
        for (let x = 0; x < qrData.length; ++x) {
            for (let y = 0; y < qrData.length; ++y) {
                if (qrData[y][x]) {
                    bitmap.writePixel(x * this.QR_ITEM_SIZE, y * this.QR_ITEM_SIZE, this.QR_ITEM_SIZE);
                }
            }
        }

        return bitmap.toString();
    }

    private generateQRCodeWithText(text: string[], data: string, width: number, height: number): string {
        // compress data
        var base64TextConverter = new Base64TextConverter();
        let dataStrZIP: string = base64TextConverter.toCompressedBase64(data);

        // prepare QR
        let qrData: any = new QRCode.generate(dataStrZIP);
        let qrSize: number = qrData.length * this.QR_ITEM_SIZE;

        // prepare text
        let textHeight: number = text.length === 0 ? 0 : this.FONT_LINE_HEIGHT * (text.length + 1);

        // create bitmap
        let bitmap: PngImageBuilder = new PngImageBuilder(width, height);

        // draw QR
        let qrOffsetX: number = Math.floor((bitmap.getWidth() - qrSize) / 2);
        let qrOffsetY: number = Math.floor((bitmap.getHeight() - qrSize + textHeight) / 2);
        for (let x = 0; x < qrData.length; ++x) {
            for (let y = 0; y < qrData.length; ++y) {
                if (qrData[y][x]) {
                    bitmap.writePixel(qrOffsetX + x * this.QR_ITEM_SIZE, qrOffsetY + y * this.QR_ITEM_SIZE, this.QR_ITEM_SIZE);
                }
            }
        }

        // draw text
        for (let i = 0; i < text.length; ++i) {
            let x = Math.floor(bitmap.getWidth() / 2);
            let y = Math.floor((bitmap.getHeight() - qrSize - textHeight) / 2) + this.FONT_LINE_HEIGHT * i;
            bitmap.writeText(x, y, text[i], this.FONT_LINE_HEIGHT);
        }

        return bitmap.toString();
    }

    private createQrCodeInfo(request: RequestRoot) {
        let personName: string = '';
        if (request.features instanceof RequestFeatures) {
            const r = request.features as RequestFeatures;
            personName =
                r.applicant.personIdentity.fullName.lastName + ' ' + r.applicant.personIdentity.fullName.firstName.substr(0, 1) + '.';
            if (r.applicant.personIdentity.fullName.middleName.value) {
                personName += ' ' + r.applicant.personIdentity.fullName.middleName.value.substr(0, 1) + '.';
            }
        }
        if (request.features instanceof RequestFeaturesV2) {
            const r = request.features as RequestFeaturesV2;
            personName =
                r.applicant.personIdentity.fullName.lastName + ' ' + r.applicant.personIdentity.fullName.firstName.substr(0, 1) + '.';
            if (r.applicant.personIdentity.fullName.middleName.value) {
                personName += ' ' + r.applicant.personIdentity.fullName.middleName.value.substr(0, 1) + '.';
            }
        }
        return ['Штрих-код, содержащий сведения заявления', 'о выдаче паспорта нового поколения', personName];
    }
}

export enum QRCodeError {
    Unknown = 0,
    TooLarge = 1,
}
